import React, { useEffect, useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import cspmService from '@/services/cspm.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useWarehouses } from '@/hooks/useInventory';
import { useCspmPermissions } from '@/hooks/useCspmPermissions';
import { CspmSourceDocumentSelect } from '@/components/cspm/CspmSourceDocumentSelect';
import { isCspmCreateRoute } from '@/utils/cspmCreateRoute';
import {
  CSPM_CONDITIONS,
  CSPM_CONDITION_LABELS,
  CSPM_SOURCE_TYPES,
  CSPM_SOURCE_TYPE_LABELS,
  enumOptions,
  type CustomerPropertyReceipt,
  type CustomerPropertyReturnLine,
} from '@/constants/cspm';
import { EngineCspmReturnPrint } from '@/components/print/EngineLetterheadPrints';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_RETURN } from '@/constants/document-entity-types';

const { Text } = Typography;

type LineRow = CustomerPropertyReturnLine & {
  key: string;
  source_qty?: number;
  source_line_number?: number;
};

const emptyLine = (): LineRow => ({
  key: `new-${Date.now()}-${Math.random()}`,
  description: '',
  quantity: 1,
  uom: 'PCS',
  condition_code: 'GOOD',
});

const linesFromReceipt = (receipt: CustomerPropertyReceipt): LineRow[] =>
  (receipt.lines || [])
    .filter((ln) => ln.id && ln.custody_status !== 'RETURNED')
    .map((ln) => {
      const accepted = Number(ln.accepted_qty) || 0;
      const qty = accepted > 0 ? accepted : Number(ln.received_qty) || 0;
      return {
        key: `rcpt-${ln.id}`,
        receipt_line_id: ln.id,
        item_id: ln.item_id || null,
        description: ln.description || '',
        quantity: qty,
        source_qty: qty,
        source_line_number: ln.line_number,
        uom: ln.uom || 'PCS',
        serial_number: ln.serial_number || null,
        heat_number: ln.heat_number || null,
        lot_number: ln.lot_number || null,
        batch_number: ln.batch_number || null,
        condition_code: ln.condition_code || 'GOOD',
        condition_notes: null,
      };
    })
    .filter((ln) => ln.quantity > 0);

const hasUserLines = (rows: LineRow[]) =>
  rows.some((ln) => String(ln.description || '').trim() || ln.receipt_line_id);

const CustomerPropertyReturnPage: React.FC = () => {
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();
  const isNew = isCspmCreateRoute(routeId, location.pathname);
  const id = isNew ? undefined : routeId;
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const cspmPerms = useCspmPermissions();
  const [form] = Form.useForm();
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);
  const [printOpen, setPrintOpen] = useState(false);
  const watchedCustomerId = Form.useWatch('customer_id', form);
  const watchedSourceType = Form.useWatch('source_document_type', form);
  const watchedReceiptId = Form.useWatch('receipt_id', form) as string | undefined;

  const { data: customers = [] } = useActiveCustomers();
  const { data: warehouses = [] } = useWarehouses();

  const { data: ret, isLoading } = useQuery({
    queryKey: ['cspm', 'return', id],
    queryFn: () => cspmService.getReturn(id!),
    enabled: !isNew && Boolean(id),
  });

  const isDraft = isNew || ret?.status === 'Draft';

  const { data: postedReceipts, isFetching: receiptsLoading } = useQuery({
    queryKey: ['cspm', 'receipts', 'posted', watchedCustomerId || 'all'],
    queryFn: () =>
      cspmService.listReceipts({
        customer_id: watchedCustomerId || undefined,
        status: 'Posted',
        take: 200,
      }),
    enabled: isDraft,
  });

  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name?: string; name?: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name || c.name || c.id,
      })),
    [customers]
  );

  const receiptOptions = useMemo(() => {
    const customerLabel = new Map(customerOptions.map((c) => [c.value, c.label]));
    return (postedReceipts?.rows ?? []).map((r) => {
      const party = r.customer_name || customerLabel.get(r.customer_id);
      const no = r.receipt_number || r.id;
      return { value: r.id, label: watchedCustomerId || !party ? no : `${no} · ${party}` };
    });
  }, [postedReceipts, customerOptions, watchedCustomerId]);

  const [loadingReceiptLines, setLoadingReceiptLines] = useState(false);

  const applyReceipt = async (receiptId: string | undefined) => {
    if (!receiptId) return;
    setLoadingReceiptLines(true);
    try {
      const receipt = await cspmService.getReceipt(receiptId);
      if (!receipt) return;
      const current = form.getFieldsValue(['warehouse_id', 'source_document_type', 'source_document_id']);
      form.setFieldsValue({
        customer_id: receipt.customer_id,
        warehouse_id: current.warehouse_id || receipt.warehouse_id,
        ...(current.source_document_type
          ? {}
          : {
              source_document_type: receipt.source_document_type,
              source_document_id: receipt.source_document_id,
            }),
      });
      const fromReceipt = linesFromReceipt(receipt);
      if (!fromReceipt.length) {
        message.info(`All lines on ${receipt.receipt_number} are already returned.`);
        return;
      }
      const apply = () => {
        setLines(fromReceipt);
        message.success(`${fromReceipt.length} line(s) loaded from ${receipt.receipt_number}`);
      };
      if (hasUserLines(lines)) {
        modal.confirm({
          title: `Replace return lines with the lines from ${receipt.receipt_number}?`,
          okText: 'Replace',
          onOk: apply,
        });
      } else {
        apply();
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Could not load receipt lines');
    } finally {
      setLoadingReceiptLines(false);
    }
  };

  const warehouseOptions = useMemo(
    () =>
      (warehouses as Array<{ id: string; warehouse_name?: string; name?: string }>).map((w) => ({
        value: w.id,
        label: w.warehouse_name || w.name || w.id,
      })),
    [warehouses]
  );

  const customerName =
    customerOptions.find((c) => c.value === (ret?.customer_id || watchedCustomerId))
      ?.label || ret?.customer_name;

  useEffect(() => {
    if (!ret) return;
    form.setFieldsValue({
      customer_id: ret.customer_id,
      receipt_id: ret.receipt_id,
      source_document_type: ret.source_document_type,
      source_document_id: ret.source_document_id,
      return_datetime: ret.return_datetime ? dayjs(ret.return_datetime) : dayjs(),
      warehouse_id: ret.warehouse_id,
      reason: ret.reason,
      remarks: ret.remarks,
    });
    setLines(
      (ret.lines || []).map((ln, idx) => ({
        ...ln,
        key: ln.id || `line-${idx}`,
      }))
    );
  }, [ret, form]);

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.createReturn(body),
    onSuccess: (row) => {
      message.success('Return draft created');
      queryClient.invalidateQueries({ queryKey: ['cspm'] });
      if (row?.id) navigate(`/cspm/returns/${row.id}`, { replace: true });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const updateMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => cspmService.updateReturn(id!, body),
    onSuccess: () => {
      message.success('Return draft saved');
      queryClient.invalidateQueries({ queryKey: ['cspm'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const postMut = useMutation({
    mutationFn: () => cspmService.postReturn(id!),
    onSuccess: () => {
      message.success('Return posted');
      queryClient.invalidateQueries({ queryKey: ['cspm'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Post failed'),
  });

  const ackLinkMut = useMutation({
    mutationFn: () => cspmService.issueReturnAckLink(id!),
    onSuccess: (data) => {
      const token = data?.acknowledgement_url_token;
      if (!token) {
        message.warning('No acknowledgement token returned');
        return;
      }
      const url = `${window.location.origin}/cspm/public/returns/${token}/acknowledge`;
      void navigator.clipboard?.writeText(url);
      modal.info({
        title: 'Customer acknowledgement link',
        content: (
          <div>
            <p>Link copied to clipboard (if permitted):</p>
            <Input.TextArea value={url} readOnly autoSize />
            {data?.customer_ack_at ? (
              <p style={{ marginTop: 12 }}>
                Already acknowledged by {data.customer_ack_name || '—'} at {String(data.customer_ack_at)}.
              </p>
            ) : (
              <p style={{ marginTop: 12 }}>Share this link with the customer. Missing ack is a WARNING only.</p>
            )}
          </div>
        ),
        width: 560,
      });
      queryClient.invalidateQueries({ queryKey: ['cspm'] });
    },
    onError: (e: any) =>
      message.error(e?.response?.data?.message || e?.message || 'Could not issue link'),
  });

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const payloadLines = lines
        .map((ln) => ({
          receipt_line_id: ln.receipt_line_id || null,
          allocation_id: ln.allocation_id || null,
          item_id: ln.item_id || null,
          description: String(ln.description || '').trim(),
          quantity: Number(ln.quantity) || 0,
          uom: ln.uom || 'PCS',
          serial_number: ln.serial_number || null,
          heat_number: ln.heat_number || null,
          lot_number: ln.lot_number || null,
          batch_number: ln.batch_number || null,
          condition_code: ln.condition_code || null,
          condition_notes: ln.condition_notes?.trim() || null,
          remarks: ln.remarks || null,
        }))
        .filter((ln) => ln.description && ln.quantity > 0);
      if (!payloadLines.length) {
        message.error('At least one return line is required');
        return;
      }
      const body = {
        ...values,
        return_datetime: values.return_datetime
          ? dayjs(values.return_datetime).toISOString()
          : undefined,
        lines: payloadLines,
      };
      if (isNew) createMut.mutate(body);
      else updateMut.mutate(body);
    } catch (e: any) {
      message.error(e?.message || 'Please complete required fields');
    }
  };

  const updateLine = (key: string, patch: Partial<LineRow>) => {
    setLines((prev) => prev.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)));
  };

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/cspm')}>
                Back
              </Button>
              <div>
                <h2 style={{ margin: 0 }}>
                  {isNew ? 'New externally owned property return' : ret?.return_number || 'Return'}
                </h2>
                {!isNew && ret?.status && <Tag>{ret.status}</Tag>}
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              {!isNew && (
                <Button icon={<PrinterOutlined />} onClick={() => setPrintOpen(true)}>
                  Print
                </Button>
              )}
              {!isNew && ret?.status === 'Posted' && cspmPerms.canReturn && (
                <Button
                  loading={ackLinkMut.isPending}
                  onClick={() => ackLinkMut.mutate()}
                >
                  Customer ack link
                </Button>
              )}
              {isDraft && cspmPerms.canReturn && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={createMut.isPending || updateMut.isPending}
                  onClick={onSave}
                >
                  {isNew ? 'Create draft' : 'Save draft'}
                </Button>
              )}
              {!isNew && ret?.status === 'Draft' && cspmPerms.canReturn && (
                <Button
                  loading={postMut.isPending}
                  onClick={() =>
                    modal.confirm({
                      title: 'Post this return to customer?',
                      onOk: () => postMut.mutateAsync(),
                    })
                  }
                >
                  Post return
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        <Card title="Header">
          <Form
            form={form}
            layout="vertical"
            disabled={!isDraft}
            initialValues={{ return_datetime: dayjs() }}
          >
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="customer_id" label="Customer" rules={[{ required: true }]}>
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={customerOptions}
                    onChange={(customerId) => {
                      const receiptId = form.getFieldValue('receipt_id');
                      const receipt = postedReceipts?.rows.find((r) => r.id === receiptId);
                      if (receipt && receipt.customer_id !== customerId) {
                        form.setFieldsValue({ receipt_id: undefined });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="receipt_id"
                  label="Source EOP receipt"
                  extra={isDraft ? 'Selecting a receipt loads its open lines' : undefined}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select posted EOP receipt"
                    loading={receiptsLoading || loadingReceiptLines}
                    notFoundContent={receiptsLoading ? 'Loading…' : 'No posted EOP receipts'}
                    options={receiptOptions}
                    onChange={(receiptId?: string) => void applyReceipt(receiptId)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="warehouse_id" label="Warehouse">
                  <Select allowClear showSearch optionFilterProp="label" options={warehouseOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="return_datetime" label="Return datetime" rules={[{ required: true }]}>
                  <DatePicker showTime style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="source_document_type" label="Source type">
                  <Select
                    allowClear
                    options={enumOptions(CSPM_SOURCE_TYPES, CSPM_SOURCE_TYPE_LABELS)}
                    onChange={() => form.setFieldsValue({ source_document_id: undefined })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="source_document_id" label="Source document">
                  <CspmSourceDocumentSelect
                    sourceType={watchedSourceType}
                    customerId={watchedCustomerId}
                    onValidated={(result) => {
                      if (result?.customer_id) {
                        form.setFieldsValue({ customer_id: result.customer_id });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="reason" label="Reason">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="remarks" label="Remarks">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Card>

        <Card
          title="Return lines"
          extra={
            isDraft ? (
              <Space>
                {watchedReceiptId && (
                  <Button
                    loading={loadingReceiptLines}
                    onClick={() => void applyReceipt(watchedReceiptId)}
                  >
                    Reload lines from receipt
                  </Button>
                )}
                <Button icon={<PlusOutlined />} onClick={() => setLines((p) => [...p, emptyLine()])}>
                  Add line
                </Button>
              </Space>
            ) : null
          }
        >
          <Table
            rowKey="key"
            dataSource={lines}
            pagination={false}
            scroll={{ x: 1000 }}
            columns={[
              {
                title: '#',
                width: 48,
                render: (_: unknown, __: LineRow, idx: number) => idx + 1,
              },
              {
                title: 'Description',
                render: (_: unknown, row: LineRow) => (
                  <>
                    {isDraft ? (
                      <Input
                        value={row.description}
                        onChange={(e) => updateLine(row.key, { description: e.target.value })}
                      />
                    ) : (
                      row.description
                    )}
                    {row.receipt_line_id && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        From receipt line
                        {row.source_line_number != null ? ` ${row.source_line_number}` : ''}
                        {row.source_qty != null ? ` · received ${row.source_qty}` : ''}
                      </Text>
                    )}
                  </>
                ),
              },
              {
                title: 'Qty',
                width: 110,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <InputNumber
                      min={0.0001}
                      max={row.source_qty}
                      style={{ width: '100%' }}
                      value={row.quantity}
                      onChange={(v) => updateLine(row.key, { quantity: Number(v) || 0 })}
                    />
                  ) : (
                    row.quantity
                  ),
              },
              {
                title: 'UOM',
                width: 90,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Input value={row.uom} onChange={(e) => updateLine(row.key, { uom: e.target.value })} />
                  ) : (
                    row.uom
                  ),
              },
              {
                title: 'Condition',
                width: 240,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <>
                      <Select
                        style={{ width: '100%' }}
                        value={row.condition_code || undefined}
                        options={enumOptions(CSPM_CONDITIONS, CSPM_CONDITION_LABELS)}
                        onChange={(condition_code) => updateLine(row.key, { condition_code })}
                      />
                      <Input.TextArea
                        style={{ marginTop: 6 }}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        maxLength={2000}
                        placeholder="Condition note"
                        value={row.condition_notes ?? ''}
                        onChange={(e) => updateLine(row.key, { condition_notes: e.target.value })}
                      />
                    </>
                  ) : (
                    <>
                      {CSPM_CONDITION_LABELS[row.condition_code as keyof typeof CSPM_CONDITION_LABELS] ||
                        row.condition_code ||
                        '—'}
                      {row.condition_notes ? (
                        <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', whiteSpace: 'pre-wrap' }}>
                          {row.condition_notes}
                        </div>
                      ) : null}
                    </>
                  ),
              },
              {
                title: 'S/N',
                width: 120,
                render: (_: unknown, row: LineRow) =>
                  isDraft ? (
                    <Input
                      value={row.serial_number || ''}
                      onChange={(e) => updateLine(row.key, { serial_number: e.target.value })}
                    />
                  ) : (
                    row.serial_number || '—'
                  ),
              },
              ...(isDraft
                ? [
                    {
                      title: '',
                      width: 48,
                      render: (_: unknown, row: LineRow) => (
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={lines.length <= 1}
                          onClick={() => setLines((prev) => prev.filter((l) => l.key !== row.key))}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>

        <EntityAttachmentsCard
          entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_RETURN}
          entityId={isNew ? null : id}
          title="Return evidence"
          readOnly={!isDraft}
          saveFirstMessage="Save the return draft before attaching files."
        />
      </Space>

      <Modal
        open={printOpen}
        onCancel={() => setPrintOpen(false)}
        footer={null}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        {ret && (
          <EngineCspmReturnPrint
            data={ret}
            customerName={customerName}
            onClose={() => setPrintOpen(false)}
          />
        )}
      </Modal>
    </div>
  );
};

export default CustomerPropertyReturnPage;
